import React from "react";
import { FiHelpCircle } from "react-icons/fi";
import "./PassportControlMenu.css";
import PresetSelectDropdown from "../../../../components/ui/PresetSelectDropdown";
import Tooltip from "../../../../components/common/Tooltip/Tooltip";
import { getCategoryPresetPickerCopy } from "../../../../config/dashboard/dashboardCategoryModalConfig";
import {
  PASSPORT_CATEGORY_ID,
  resolvePassportSpec,
  clampRatio,
  getPassportDpiDisplay,
  backgroundCompactLabel,
  getPassportBackgroundTooltip,
} from "../../../../utils/passportPresetUtils";

const PassportInlineLabel = ({ label, tooltip, boundaryRef, tooltipAlign = "start" }) => (
  <div className="passport-inline-label-row">
    <span>{label}</span>
    {tooltip ? (
      <Tooltip
        content={tooltip}
        position="top"
        align={tooltipAlign}
        size="menu"
        boundaryRef={boundaryRef}
        flip={false}
      >
        <button
          type="button"
          className="passport-info-trigger"
          aria-label={`More about ${label}`}
        >
          <FiHelpCircle aria-hidden="true" />
        </button>
      </Tooltip>
    ) : null}
  </div>
);

const PassportControlMenu = ({
  selectedPreset = null,
  countryOptions = [],
  onCountryChange,
  onSuggestCrop,
  onGenerate,
  busy = false,
  presetsLoading = false,
  suggestedCrop = null,
  beforeActions = null,
  generateLabel = "Generate",
  generateDisabled = false,
  hideActions = false,
}) => {
  const pickerCopy = React.useMemo(
    () => getCategoryPresetPickerCopy({ category_id: PASSPORT_CATEGORY_ID }),
    []
  );
  const spec = React.useMemo(
    () => resolvePassportSpec(selectedPreset),
    [selectedPreset]
  );
  const helperText = selectedPreset?.helper_text || {};
  const menuRef = React.useRef(null);
  const backgroundTooltip = React.useMemo(
    () => getPassportBackgroundTooltip(spec, helperText, selectedPreset?.name),
    [spec, helperText, selectedPreset?.name]
  );

  const [headRatioMin, setHeadRatioMin] = React.useState(spec.head_h_ratio_min);
  const [headRatioMax, setHeadRatioMax] = React.useState(spec.head_h_ratio_max);
  const [eyeLineFromTop, setEyeLineFromTop] = React.useState(spec.eye_line_from_top);
  const [crop, setCrop] = React.useState(null);

  React.useEffect(() => {
    setHeadRatioMin(spec.head_h_ratio_min);
    setHeadRatioMax(spec.head_h_ratio_max);
    setEyeLineFromTop(spec.eye_line_from_top);
    setCrop(null);
  }, [selectedPreset?.id, spec.head_h_ratio_min, spec.head_h_ratio_max, spec.eye_line_from_top]);

  React.useEffect(() => {
    if (suggestedCrop && typeof suggestedCrop === "object") {
      const { crop_left, crop_top, crop_w, crop_h } = suggestedCrop;
      if (
        crop_left != null &&
        crop_top != null &&
        crop_w != null &&
        crop_h != null
      ) {
        setCrop({ crop_left, crop_top, crop_w, crop_h });
      }
    }
  }, [suggestedCrop]);

  const handleCountrySelect = (preset) => {
    if (preset && typeof onCountryChange === "function") {
      onCountryChange(preset);
    }
  };

  const buildBaseOpts = () => ({
    w_mm: Number(spec.w_mm),
    h_mm: Number(spec.h_mm),
    dpi: Number(spec.dpi),
    head_h_ratio_min: clampRatio(headRatioMin),
    head_h_ratio_max: clampRatio(headRatioMax),
    eye_line_from_top: clampRatio(eyeLineFromTop),
  });

  const handleSuggestCrop = () => {
    if (typeof onSuggestCrop !== "function") return;
    onSuggestCrop(buildBaseOpts());
  };

  const handleGenerate = () => {
    if (typeof onGenerate !== "function") return;
    const opts = {
      ...buildBaseOpts(),
      bg_hex: spec.bg_hex || "#FFFFFF",
      white_background: spec.white_background !== false,
      print_sheet: false,
      output_format: "png",
    };
    if (crop) {
      opts.crop_left = crop.crop_left;
      opts.crop_top = crop.crop_top;
      opts.crop_w = crop.crop_w;
      opts.crop_h = crop.crop_h;
    }
    onGenerate(opts);
  };

  const handleHeadMinChange = (value) => {
    setHeadRatioMin(clampRatio(value));
  };

  const handleHeadMaxChange = (value) => {
    setHeadRatioMax(clampRatio(value));
  };

  const bgHex = spec.bg_hex || "#FFFFFF";
  const formatMm = (value) => {
    const n = Number(value);
    if (Number.isNaN(n)) return "";
    return Number.isInteger(n) ? String(n) : String(n);
  };

  return (
    <div ref={menuRef} className="passport-control-menu-container">
      <div className="passport-field">
        <PresetSelectDropdown
          id="passport-size-select"
          label="Size"
          presets={countryOptions}
          selectedPreset={selectedPreset}
          onSelect={handleCountrySelect}
          loading={presetsLoading}
          disabled={busy}
          pickerCopy={pickerCopy}
          categoryId={PASSPORT_CATEGORY_ID}
          className="passport-preset-select"
        />
        <p className="passport-size-dimensions">
          <span>Width (mm): {formatMm(spec.w_mm)}</span>
          <span>Height (mm): {formatMm(spec.h_mm)}</span>
        </p>
        {helperText.size ? (
          <p className="passport-helper-text">{helperText.size}</p>
        ) : null}
      </div>

      <div className="passport-field">
        <div className="passport-row">
          <div className="passport-inline-field">
            <PassportInlineLabel
              label="DPI"
              tooltip={helperText.dpi}
              boundaryRef={menuRef}
              tooltipAlign="end"
            />
            <input
              type="text"
              readOnly
              disabled
              className="passport-input passport-input--readonly"
              value={getPassportDpiDisplay(spec)}
              tabIndex={-1}
            />
          </div>
          <div className="passport-inline-field">
            <PassportInlineLabel
              label="Background"
              tooltip={backgroundTooltip}
              boundaryRef={menuRef}
              tooltipAlign="start"
            />
            <div
              className="passport-readonly-box passport-readonly-box--muted passport-bg-box"
              aria-readonly="true"
            >
              <span
                className="passport-bg-swatch passport-bg-swatch--compact"
                style={{ backgroundColor: bgHex }}
                aria-hidden="true"
              />
              <span className="passport-bg-label">{backgroundCompactLabel(spec)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="passport-field">
        <p className="passport-section-label">Head height ratio</p>
        <p className="passport-field-meta">
          Range: {clampRatio(headRatioMin).toFixed(2)} – {clampRatio(headRatioMax).toFixed(2)}
        </p>
        <div className="passport-row">
          <label className="passport-inline-field">
            <input
              type="number"
              min={0}
              max={1}
              step={0.01}
              value={headRatioMin}
              onChange={(e) => handleHeadMinChange(parseFloat(e.target.value))}
              className="passport-input passport-input--no-spinner"
            />
          </label>
          <label className="passport-inline-field">
            <input
              type="number"
              min={0}
              max={1}
              step={0.01}
              value={headRatioMax}
              onChange={(e) => handleHeadMaxChange(parseFloat(e.target.value))}
              className="passport-input passport-input--no-spinner"
            />
          </label>
        </div>
        {helperText.head_ratio ? (
          <p className="passport-helper-text">{helperText.head_ratio}</p>
        ) : null}
      </div>

      <div className="passport-field">
        <p className="passport-section-label">Eye line from top</p>
        <p className="passport-field-meta">
          Position: {clampRatio(eyeLineFromTop).toFixed(2)}
        </p>
        <input
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={eyeLineFromTop}
          onChange={(e) => setEyeLineFromTop(clampRatio(parseFloat(e.target.value)))}
          className="passport-slider"
        />
        {helperText.eye_line ? (
          <p className="passport-helper-text">{helperText.eye_line}</p>
        ) : null}
      </div>

      {crop ? (
        <div className="passport-crop-summary">
          <p className="passport-section-label">Suggested crop</p>
          <p className="passport-crop-text">
            x:{crop.crop_left} y:{crop.crop_top} · {crop.crop_w}×{crop.crop_h}
          </p>
          <button
            type="button"
            className="passport-link-btn"
            onClick={() => setCrop(null)}
          >
            Clear crop
          </button>
        </div>
      ) : null}

      {beforeActions}

      {!hideActions ? (
        <div className="passport-actions">
          <button
            type="button"
            className="passport-secondary-btn"
            onClick={handleSuggestCrop}
            disabled={busy}
          >
            {busy ? "Working…" : "Suggest crop"}
          </button>
          <button
            type="button"
            className="passport-primary-btn"
            onClick={handleGenerate}
            disabled={busy || generateDisabled}
          >
            {busy ? "Generating…" : generateLabel}
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default PassportControlMenu;
